import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Plus, Trash2, X, MapPin, Lock, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { apiError, dateShort } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionTitle, Field, Pill, EmptyState } from "@/components/Bits";

const input = "w-full rounded-sm border border-line bg-white px-3 py-2 text-sm outline-none focus:border-lime";

export const TYPE_TONE = {
  Installation: "bg-lime text-ink",
  Delivery: "bg-[#EDEFC4] text-ink",
  Presentation: "bg-[#DDE3C8] text-ink",
  "Client Meeting": "bg-[#E8E6DE] text-ink",
  "Site Visit": "bg-[#E8E6DE] text-ink",
  "Order Deadline": "bg-[#F6DED6] text-[#A8492E]",
  "Approval Deadline": "bg-[#F6DED6] text-[#A8492E]",
  "Vendor Appointment": "bg-line text-stone",
  Milestone: "bg-ink text-white",
  Photography: "bg-[#EDEFC4] text-ink",
  Custom: "bg-line text-stone",
};

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const iso = (d) => d.toISOString().slice(0, 10);
const timeOf = (e) =>
  e.all_day ? "All day" : new Date(e.start).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function monthGrid(cursor) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - offset);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function EventForm({ projects, initial, projectId, onSaved, onClose }) {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(
    initial || {
      title: "", event_type: "Client Meeting", project_id: projectId || "", start: "",
      end: "", all_day: false, location: "", description: "", visibility: "internal",
    }
  );

  useEffect(() => {
    api.get("/calendar/meta").then((r) => setTypes(r.data.event_types)).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.title || !form.start) return toast.error("A title and a date are needed");
    const payload = {
      ...form,
      project_id: form.project_id || null,
      start: form.start.length === 10 ? `${form.start}T09:00:00+00:00` : `${form.start}:00+00:00`,
      end: form.end ? (form.end.length === 10 ? `${form.end}T17:00:00+00:00` : `${form.end}:00+00:00`) : null,
    };
    try {
      if (initial?.id) await api.put(`/calendar/events/${initial.id}`, payload);
      else await api.post("/calendar/events", payload);
      toast.success(initial?.id ? "Event updated" : "Event added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-6" data-testid="event-form-modal">
      <div className="absolute inset-0 bg-ink/35 backdrop-blur-sm" onClick={onClose} />
      <div className="card-surface relative max-h-[92vh] w-full max-w-xl overflow-auto p-7">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-serif text-2xl text-ink">{initial?.id ? "Edit event" : "New event"}</h3>
          <button onClick={onClose} aria-label="Close"><X className="h-4 w-4 text-fog" strokeWidth={1.5} /></button>
        </div>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <Field label="Title" className="sm:col-span-2">
            <input data-testid="event-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} />
          </Field>
          <Field label="Type">
            <select data-testid="event-type-select" value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className={input}>
              {(types.length ? types : Object.keys(TYPE_TONE)).map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Project">
            <select data-testid="event-project-select" value={form.project_id || ""} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className={input}>
              <option value="">Studio-wide</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Starts">
            <input data-testid="event-start-input" type={form.all_day ? "date" : "datetime-local"} value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className={input} />
          </Field>
          <Field label="Ends (optional)">
            <input data-testid="event-end-input" type={form.all_day ? "date" : "datetime-local"} value={form.end || ""} onChange={(e) => setForm({ ...form, end: e.target.value })} className={input} />
          </Field>
          <Field label="Location (optional)">
            <input data-testid="event-location-input" value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} className={input} />
          </Field>
          <Field label="Visibility">
            <select data-testid="event-visibility-select" value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} className={input}>
              <option value="internal">Internal team only</option>
              <option value="client">Client visible</option>
            </select>
          </Field>
          <Field label="Description (shown to the client when visible)" className="sm:col-span-2">
            <textarea data-testid="event-description-input" rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className={input} />
          </Field>
          <label className="flex items-center gap-3 text-sm text-ink sm:col-span-2">
            <input data-testid="event-allday-toggle" type="checkbox" checked={form.all_day} onChange={(e) => setForm({ ...form, all_day: e.target.checked })} className="h-4 w-4 accent-[#BEC221]" />
            All day / multi-day window
          </label>
        </div>
        <div className="mt-7 flex gap-3">
          <button data-testid="event-save-btn" onClick={save} className="rounded-sm bg-lime px-5 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">Save event</button>
          <button onClick={onClose} className="rounded-sm border border-line px-5 py-2.5 text-sm text-ink hover:bg-line/50">Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function CalendarView({ projectId, compact }) {
  const { isStaff } = useAuth();
  const [params] = useSearchParams();
  const [view, setView] = useState(params.get("view") || (compact ? "agenda" : "month"));
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({
    project_id: projectId || "", event_type: params.get("event_type") || "", visibility: "",
  });
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const upcomingOnly = params.get("upcoming") === "1";

  const load = () =>
    api
      .get("/calendar/events", {
        params: {
          project_id: projectId || filters.project_id || undefined,
          event_type: filters.event_type || undefined,
          visibility: isStaff && filters.visibility ? filters.visibility : undefined,
          upcoming: upcomingOnly || undefined,
        },
      })
      .then((r) => setEvents(r.data))
      .catch(() => setEvents([]));

  useEffect(() => {
    load();
    if (isStaff && !projectId) api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, filters.project_id, filters.event_type, filters.visibility, upcomingOnly]);

  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const key = (e.start || "").slice(0, 10);
      (map[key] = map[key] || []).push(e);
    });
    return map;
  }, [events]);

  const remove = async (id) => {
    try {
      await api.delete(`/calendar/events/${id}`);
      toast.success("Event removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const weekDays = useMemo(() => {
    const start = new Date(cursor);
    start.setDate(cursor.getDate() - ((cursor.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [cursor]);

  const shift = (dir) => {
    const next = new Date(cursor);
    if (view === "week") next.setDate(cursor.getDate() + dir * 7);
    else next.setMonth(cursor.getMonth() + dir);
    setCursor(next);
  };

  const EventChip = ({ e }) => (
    <button
      onClick={() => (isStaff ? setEditing({ ...e, start: (e.start || "").slice(0, 16), end: (e.end || "").slice(0, 16) }) : null)}
      data-testid={`calendar-event-${e.id}`}
      className="block w-full truncate rounded-sm px-2 py-1 text-left text-[11px] transition-colors hover:opacity-80"
      style={{}}
    >
      <span className={`inline-block max-w-full truncate rounded-sm px-1.5 py-[1px] ${TYPE_TONE[e.raw_type || e.event_type] || "bg-line text-stone"}`}>
        {timeOf(e)} · {e.title}
      </span>
    </button>
  );

  return (
    <div className="space-y-7" data-testid="calendar-view">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {view !== "agenda" && (
            <>
              <button data-testid="calendar-prev" onClick={() => shift(-1)} className="rounded-sm border border-line p-2 text-stone hover:border-fog">
                <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              </button>
              <button data-testid="calendar-next" onClick={() => shift(1)} className="rounded-sm border border-line p-2 text-stone hover:border-fog">
                <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </>
          )}
          <span className="font-serif text-xl text-ink">
            {view === "agenda" ? "Coming up" : `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex rounded-sm border border-line bg-white">
            {["month", "week", "agenda"].map((v) => (
              <button
                key={v}
                data-testid={`calendar-view-${v}`}
                onClick={() => setView(v)}
                className={`px-4 py-2 text-xs uppercase tracking-[0.12em] transition-colors ${view === v ? "bg-[#EDEFC4] text-ink" : "text-stone hover:text-ink"}`}
              >
                {v}
              </button>
            ))}
          </div>
          {isStaff && (
            <button data-testid="calendar-add-event" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-sm bg-lime px-4 py-2.5 text-sm font-medium text-ink hover:bg-lime-dark">
              <Plus className="h-4 w-4" strokeWidth={1.5} /> New event
            </button>
          )}
        </div>
      </div>

      {isStaff && (
        <div className="flex flex-wrap gap-3" data-testid="calendar-filters">
          {!projectId && (
            <select data-testid="calendar-filter-project" value={filters.project_id} onChange={(e) => setFilters({ ...filters, project_id: e.target.value })} className="rounded-sm border border-line bg-white px-3 py-2 text-xs outline-none focus:border-lime">
              <option value="">All projects</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <select data-testid="calendar-filter-type" value={filters.event_type} onChange={(e) => setFilters({ ...filters, event_type: e.target.value })} className="rounded-sm border border-line bg-white px-3 py-2 text-xs outline-none focus:border-lime">
            <option value="">All event types</option>
            {Object.keys(TYPE_TONE).map((t) => <option key={t}>{t}</option>)}
          </select>
          <select data-testid="calendar-filter-visibility" value={filters.visibility} onChange={(e) => setFilters({ ...filters, visibility: e.target.value })} className="rounded-sm border border-line bg-white px-3 py-2 text-xs outline-none focus:border-lime">
            <option value="">Internal + client visible</option>
            <option value="internal">Internal only</option>
            <option value="client">Client visible</option>
          </select>
        </div>
      )}

      {view === "month" && (
        <div className="card-surface overflow-hidden" data-testid="calendar-month">
          <div className="grid grid-cols-7 border-b border-line">
            {DOW.map((d) => <div key={d} className="overline px-3 py-3">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {monthGrid(cursor).map((d, i) => {
              const key = iso(d);
              const inMonth = d.getMonth() === cursor.getMonth();
              const today = key === iso(new Date());
              return (
                <div key={i} className={`min-h-[104px] space-y-1 border-b border-r border-line p-2 ${inMonth ? "" : "bg-[#FAFAF7]"}`}>
                  <div className={`num text-[11px] ${today ? "font-medium text-lime-dark" : inMonth ? "text-stone" : "text-fog"}`}>{d.getDate()}</div>
                  {(byDay[key] || []).slice(0, 3).map((e) => <EventChip key={e.id} e={e} />)}
                  {(byDay[key] || []).length > 3 && (
                    <div className="px-2 text-[10px] text-fog">+{byDay[key].length - 3} more</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "week" && (
        <div className="card-surface grid gap-0 overflow-hidden sm:grid-cols-7" data-testid="calendar-week">
          {weekDays.map((d) => {
            const key = iso(d);
            return (
              <div key={key} className="min-h-[180px] border-b border-r border-line p-3">
                <div className="overline">{DOW[(d.getDay() + 6) % 7]} {d.getDate()}</div>
                <div className="mt-3 space-y-1">
                  {(byDay[key] || []).map((e) => <EventChip key={e.id} e={e} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {view === "agenda" && (
        events.length === 0 ? (
          <EmptyState testId="calendar-empty" title="Nothing scheduled" body="Events you add will appear here." />
        ) : (
          <div className="card-surface" data-testid="calendar-agenda">
            {events.map((e) => (
              <div key={e.id} className="flex flex-wrap items-center gap-5 border-b border-line px-6 py-4 last:border-0" data-testid={`agenda-event-${e.id}`}>
                <div className="w-20 shrink-0">
                  <div className="overline">{MONTHS[new Date(e.start).getMonth()].slice(0, 3)}</div>
                  <div className="num text-2xl text-ink">{new Date(e.start).getDate()}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm text-ink">{e.title}</span>
                    <Pill tone={TYPE_TONE[e.raw_type || e.event_type] || "bg-line text-stone"}>{e.event_type}</Pill>
                    {isStaff && (
                      <Pill tone={e.visibility === "client" ? "bg-[#EDEFC4] text-ink" : "bg-line text-stone"}>
                        {e.visibility === "client" ? "Client visible" : "Internal"}
                      </Pill>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-fog">
                    <span className="num">{timeOf(e)}</span>
                    {e.project_name && (
                      <Link to={`/projects/${e.project_id}`} className="hover:text-ink">{e.project_name}</Link>
                    )}
                    {e.location && (
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" strokeWidth={1.5} />{e.location}</span>
                    )}
                  </div>
                  {e.description && <p className="mt-2 max-w-2xl text-xs leading-relaxed text-stone">{e.description}</p>}
                </div>
                {isStaff && (
                  <div className="flex gap-2">
                    <button data-testid={`edit-event-${e.id}`} onClick={() => setEditing({ ...e, start: (e.start || "").slice(0, 16), end: (e.end || "").slice(0, 16) })} className="rounded-sm border border-line px-3 py-1.5 text-xs text-stone hover:border-lime">Edit</button>
                    <button data-testid={`delete-event-${e.id}`} onClick={() => remove(e.id)} className="rounded-sm border border-line p-1.5 text-stone hover:border-clay hover:text-clay">
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {(creating || editing) && (
        <EventForm
          projects={projects}
          projectId={projectId}
          initial={editing}
          onSaved={load}
          onClose={() => { setCreating(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

export default function CalendarPage() {
  const { isStaff } = useAuth();
  return (
    <div className="space-y-9" data-testid="calendar-page">
      <PageHeader
        overline={isStaff ? "Studio" : "Your Project"}
        title={isStaff ? "Calendar" : "Timeline"}
        subtitle={
          isStaff
            ? "Every meeting, delivery and installation across the studio."
            : "The dates that matter for your home, as they’re confirmed."
        }
        testId="calendar-header"
      />
      <CalendarView />
      {!isStaff && (
        <p className="text-xs text-fog">Your designer keeps this up to date — you’ll see a note here whenever a date is confirmed.</p>
      )}
    </div>
  );
}
